import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DESIGN_PREVIEW_MODE,
  PREVIEW_ROLES,
  getPreviewRole,
  setPreviewRole,
  getPreviewDefaultRoute,
  type PreviewRole,
} from "../../lib/previewMode";

interface NavLink {
  path: string;
  label: string;
}

interface NavSection {
  title: string;
  links: NavLink[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: "Client Portal",
    links: [
      { path: "/clientportal", label: "Login / Entry" },
      { path: "/clientportal/dashboard", label: "Dashboard" },
      { path: "/clientportal/booking", label: "Public Booking" },
      { path: "/clientportal/appointments", label: "Appointments" },
      { path: "/clientportal/intake", label: "Intake Forms" },
      { path: "/clientportal/billing", label: "Billing" },
      { path: "/clientportal/messages", label: "Messages" },
      { path: "/clientportal/documents", label: "Documents" },
      { path: "/clientportal/resources", label: "Resources" },
      { path: "/clientportal/measures", label: "Measures" },
      { path: "/clientportal/settings", label: "Settings" },
    ],
  },
  {
    title: "Staff",
    links: [
      { path: "/staff", label: "Dashboard" },
      { path: "/staff/clients", label: "Caseload" },
      { path: "/staff/supervision", label: "Supervision" },
      { path: "/staff/resources", label: "Resources" },
      { path: "/staff/messages", label: "Messages" },
      { path: "/staff/availability", label: "Calendar & Availability" },
      { path: "/staff/clinicalarchitect", label: "Clinical Architect" },
    ],
  },
  {
    title: "Admin",
    links: [
      { path: "/admin", label: "Dashboard" },
      { path: "/admin/appointments", label: "Appointments" },
      { path: "/admin/billing", label: "Billing" },
      { path: "/admin/clients", label: "Clients" },
      { path: "/admin/forms", label: "Forms" },
      { path: "/admin/forms/builder", label: "Form Builder" },
      { path: "/admin/booking-visibility", label: "Booking Visibility" },
      { path: "/admin/staff-profiles", label: "Staff Profiles" },
      { path: "/admin/documents", label: "Document tracking" },
      { path: "/admin/messages", label: "Messages" },
      { path: "/admin/availability", label: "Calendar & Availability" },
    ],
  },
  {
    title: "Marketing",
    links: [
      { path: "/marketing", label: "Dashboard" },
      { path: "/marketing/campaigns", label: "Campaigns" },
      { path: "/marketing/subscribers", label: "Subscribers" },
      { path: "/marketing/funnel-performance", label: "Funnels" },
      { path: "/marketing/ghl-sync", label: "GHL Sync" },
    ],
  },
];

export default function PreviewNavigationOverlay() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState<PreviewRole>(getPreviewRole());

  // Keep role in sync if changed from another component
  useEffect(() => {
    const sync = () => setCurrentRole(getPreviewRole());
    window.addEventListener("previewRoleChanged", sync);
    return () => window.removeEventListener("previewRoleChanged", sync);
  }, []);

  const handleNav = useCallback(
    (path: string) => {
      navigate(path);
      setOpen(false);
    },
    [navigate]
  );

  const handleSelectRole = useCallback(
    (role: PreviewRole) => {
      setPreviewRole(role);
      setCurrentRole(role);
      setRoleOpen(false);

      const defaultRoute = getPreviewDefaultRoute();
      const currentSection = "/" + location.pathname.split("/").filter(Boolean)[0];
      const defaultSection = "/" + defaultRoute.split("/").filter(Boolean)[0];

      if (currentSection !== defaultSection) {
        navigate(defaultRoute, { replace: true });
      } else {
        navigate(location.pathname, { replace: true });
      }
    },
    [navigate, location.pathname]
  );

  if (!DESIGN_PREVIEW_MODE) return null;

  const activeRoleInfo = PREVIEW_ROLES.find((r) => r.role === currentRole)!;
  const isActivePath = (path: string) => location.pathname === path;

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-[9998] bg-black/10"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Floating trigger — top-right */}
      <div className="fixed top-3 right-3 z-[9999] flex flex-col items-end gap-2">
        {/* Expanded panel */}
        {open && (
          <div className="w-[340px] max-h-[80vh] flex flex-col bg-white border border-neutral-200 rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-neutral-50 border-b border-neutral-200 shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-compass-3-line text-xs text-neutral-500" />
                </div>
                <p className="font-manrope text-[12px] font-bold text-neutral-700 uppercase tracking-widest">
                  Preview Navigation
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-neutral-200/60 transition-colors cursor-pointer"
                aria-label="Close"
              >
                <i className="ri-close-line text-sm text-neutral-500" />
              </button>
            </div>

            {/* Scrollable links */}
            <div className="overflow-y-auto px-3 py-2 space-y-3">
              {NAV_SECTIONS.map((section) => (
                <div key={section.title}>
                  <p className="font-manrope text-[10px] font-bold text-neutral-400 uppercase tracking-widest px-1.5 py-1.5">
                    {section.title}
                  </p>
                  <div className="grid grid-cols-2 gap-1">
                    {section.links.map((link) => {
                      const active = isActivePath(link.path);
                      return (
                        <button
                          key={link.path}
                          onClick={() => handleNav(link.path)}
                          className={`text-left px-2.5 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap ${
                            active
                              ? "bg-neutral-800 text-white"
                              : "bg-neutral-50 hover:bg-neutral-100 text-neutral-700"
                          }`}
                        >
                          <p
                            className={`font-manrope text-[11px] font-semibold truncate ${
                              active ? "text-white" : ""
                            }`}
                          >
                            {link.label}
                          </p>
                          <p
                            className={`font-manrope text-[9px] truncate mt-0.5 ${
                              active ? "text-white/60" : "text-neutral-400"
                            }`}
                          >
                            {link.path}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Role switcher section */}
            <div className="border-t border-neutral-200 bg-neutral-50 shrink-0">
              <button
                onClick={() => setRoleOpen(!roleOpen)}
                className="w-full flex items-center justify-between px-4 py-3 cursor-pointer"
              >
                <div className="flex items-center gap-2.5">
                  <div
                    className={`w-7 h-7 flex items-center justify-center rounded-lg border ${activeRoleInfo.color}`}
                  >
                    <i className={`${activeRoleInfo.icon} text-[11px]`} />
                  </div>
                  <div className="text-left">
                    <p className="font-manrope text-[12px] font-semibold text-neutral-800">
                      {activeRoleInfo.label}
                    </p>
                    <p className="font-manrope text-[10px] text-neutral-400">
                      Current preview role
                    </p>
                  </div>
                </div>
                <i
                  className={`ri-arrow-down-s-line text-sm text-neutral-400 transition-transform ${
                    roleOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {roleOpen && (
                <div className="px-3 pb-3 space-y-1">
                  {PREVIEW_ROLES.map((item) => {
                    const isActive = item.role === currentRole;
                    return (
                      <button
                        key={item.role}
                        onClick={() => handleSelectRole(item.role)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition-colors cursor-pointer ${
                          isActive
                            ? "bg-neutral-800 text-white"
                            : "hover:bg-white text-neutral-700"
                        }`}
                      >
                        <div
                          className={`w-6 h-6 flex items-center justify-center rounded-lg border flex-shrink-0 ${
                            isActive ? "border-white/30 bg-white/10" : item.color
                          }`}
                        >
                          <i
                            className={`${item.icon} text-[10px] ${
                              isActive ? "text-white" : ""
                            }`}
                          />
                        </div>
                        <div className="min-w-0">
                          <p
                            className={`font-manrope text-[12px] font-semibold truncate ${
                              isActive ? "text-white" : ""
                            }`}
                          >
                            {item.label}
                          </p>
                          <p
                            className={`font-manrope text-[9px] leading-snug ${
                              isActive ? "text-white/60" : "text-neutral-400"
                            }`}
                          >
                            {item.description}
                          </p>
                        </div>
                        {isActive && (
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-auto">
                            <i className="ri-check-line text-white text-xs" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer note */}
            <div className="px-4 py-2.5 border-t border-neutral-200 bg-amber-50 shrink-0">
              <p className="font-manrope text-[10px] text-amber-700 leading-snug">
                <i className="ri-information-line mr-1" />
                Design preview only. Disable <code className="font-mono bg-amber-100 px-1 rounded text-amber-800">DESIGN_PREVIEW_MODE</code> for production use.
              </p>
            </div>
          </div>
        )}

        {/* Trigger pill */}
        <button
          onClick={() => setOpen(!open)}
          className={`flex items-center gap-2 px-3.5 py-2 rounded-full border shadow-lg transition-all cursor-pointer whitespace-nowrap ${
            open
              ? "bg-neutral-800 border-neutral-700 text-white"
              : "bg-white border-neutral-200 hover:border-neutral-300 hover:shadow-xl"
          }`}
        >
          <div
            className={`w-5 h-5 flex items-center justify-center rounded-full border flex-shrink-0 ${
              open
                ? "bg-neutral-700 border-neutral-600 text-neutral-300"
                : activeRoleInfo.color
            }`}
          >
            <i className={`${activeRoleInfo.icon} text-[10px]`} />
          </div>
          <span
            className={`font-manrope text-[11px] font-semibold ${
              open ? "text-white" : "text-neutral-700"
            }`}
          >
            Preview
          </span>
          <span
            className={`font-manrope text-[9px] font-medium px-1.5 py-0.5 rounded-full ${
              open ? "text-neutral-400 bg-neutral-700" : "text-neutral-400 bg-neutral-100"
            }`}
          >
            Nav
          </span>
          <i
            className={`ri-arrow-down-s-line text-xs transition-transform ${
              open ? "rotate-180 text-neutral-400" : "text-neutral-400"
            }`}
          />
        </button>
      </div>
    </>
  );
}
